/**
 * Updates the recently viewed products in localStorage.
 *
 * Yoke: besides the legacy ordered list of product ids (used by the
 * predictive-search "search by id" flow), we keep a richer v2 list of
 * per-COLOR entries — viewing Black then White of the same tee produces two
 * entries, each with its own image + variant URL — so the Recently viewed
 * rail can show every color the shopper looked at.
 */
export class RecentlyViewed {
  /** @static @constant {string} Legacy key: ordered unique product ids */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {string} V2 key: [{id, color, title, url, img}] */
  static #ENTRIES_KEY = 'viewedProductsV2';
  /** @static @constant {number} The maximum number of products to store */
  static #MAX_PRODUCTS = 8;
  /** @static @constant {number} The maximum number of per-color entries */
  static #MAX_ENTRIES = 8;

  /**
   * Adds a product to the recently viewed products list.
   * @param {string} productId - The ID of the product to add.
   * @param {{color: ?string, title: string, url: string, img: string}} [entry]
   *   Optional per-color entry (Yoke v2).
   */
  static addProduct(productId, entry = null) {
    let viewedProducts = this.getProducts();

    viewedProducts = viewedProducts.filter((/** @type {string} */ id) => id !== productId);
    viewedProducts.unshift(productId);
    viewedProducts = viewedProducts.slice(0, this.#MAX_PRODUCTS);

    localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));

    if (entry) {
      let entries = this.getEntries().filter(
        (e) => !(e.id === String(productId) && e.color === entry.color),
      );
      entries.unshift({ id: String(productId), ...entry });
      // Drop entries of products that fell off the legacy list so both stay in sync.
      const keptIds = new Set(viewedProducts.map(String));
      entries = entries.filter((e) => keptIds.has(e.id)).slice(0, this.#MAX_ENTRIES);
      localStorage.setItem(this.#ENTRIES_KEY, JSON.stringify(entries));
    }
  }

  static clearProducts() {
    localStorage.removeItem(this.#STORAGE_KEY);
    localStorage.removeItem(this.#ENTRIES_KEY);
  }

  /**
   * Retrieves the list of recently viewed products from session storage.
   * @returns {string[]} The list of viewed products.
   */
  static getProducts() {
    try {
      return JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
    } catch {
      return [];
    }
  }

  /**
   * Retrieves the per-color entries (most recent first).
   * @returns {{id: string, color: ?string, title: string, url: string, img: string}[]}
   */
  static getEntries() {
    try {
      return JSON.parse(localStorage.getItem(this.#ENTRIES_KEY) || '[]');
    } catch {
      return [];
    }
  }
}
